import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { NotFoundPage } from './NotFoundPage';
import { getCity } from '../data/cityData';
import { BUSINESS_ID, cityPath } from '../utils/seo';
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/full-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}, {
  label: 'Hybrid Ceramic Spray',
  to: '/services/hybrid-ceramic-spray'
}];
export function CityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const city = slug ? getCity(slug) : undefined;
  // An unknown city slug used to silently redirect to the homepage, which
  // search engines read as a soft 404 (a real page answering an invalid URL).
  // Serve the noindexed 404 instead.
  if (!city) {
    return <NotFoundPage data-id="element-625" data-mp="1wyf6y70mv2wq" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = cityPath(city.slug);
  // Described as a Service delivered to this city rather than a LocalBusiness
  // located in it — Mike's Pro Detail is mobile and has no storefront anywhere.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Mobile Car Detailing in ${city.name}, CA`,
    serviceType: 'Mobile Car Detailing',
    description: city.metaDescription,
    url: `${baseUrl}${path}`,
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Locations',
    item: `${baseUrl}/locations`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} cityName={city.name} data-id="element-626" data-mp="1sug5aa9w2ep0">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-627" data-mp="22nwvz5eqbdog" data-mpi={i}>{p}</p>)}

      <h2 data-id="element-628" data-mp="1xmhchhs79f86">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-629" data-mp="1xnsog4vsttad">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-630" data-mp="1iy4r1eqqnerx">
        <li className="flex items-start" data-id="element-631" data-mp="1iywd9yyg405j">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-632" data-mp="1ewj4f1oejm4j" />
          <span data-id="element-633" data-mp="1ev82l7eioog2">
            <strong data-id="element-634" data-mp="1eyvuy38874t7">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-635" data-mp="2qbewbpc27k31">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-636" data-mp="2xuo3rd1098pg" />
          <span data-id="element-637" data-mp="2xxk2v4ola3fh">
            <strong data-id="element-638" data-mp="32u0ptvk1ybqz">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-639" data-mp="2nz4a6x9wym3v">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-640" data-mp="2svkybrh450gy" />
          <span data-id="element-641" data-mp="2sxxmwnhdm92z">
            <strong data-id="element-642" data-mp="3glyu9t2tjjgt">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-643" data-mp="3lmusbq0hobeb">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-644" data-mp="36wnly8nraitz" />
          <span data-id="element-645" data-mp="3buywkllvkxd9">
            <strong data-id="element-646" data-mp="3bugq6w5i12rm">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>

      <h2 data-id="element-647" data-mp="85vm7m7snsry">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-648" data-mp="87gf77egn3np">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and detailing equipment. We require
        access to a standard electrical outlet at the service location.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-649" data-mp="21r8jg4oxp1ra">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-650" data-mp="2zc4k9erslmcj">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-651" data-mp="2pnmpbe6729gt" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-652" data-mp="2kojippakfv6q">Explore Our Detailing Services</h2>
      <p data-id="element-653" data-mp="2kni2z7ilukio">
        Learn more about each of our professional mobile detailing services
        available in {city.name}, or head back to our{' '}
        <Link to="/" data-id="element-654" data-mp="3i5s1oxhcdht3">mobile detailing homepage</Link> for full pricing and
        packages:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-655" data-mp="3dbdywa0co8ee">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-656" data-mp="3pngfcxrsrrip">
            {s.label} &rarr;
          </Link>)}
      </div>

      <h2 data-id="element-657" data-mp="3h1c2j4w5lyh">Book Mobile Detailing in {city.name}</h2>
      <p data-id="element-658" data-mp="8fbbx71k9aqf">
        Call or text Mike's Pro Detail at{' '}
        <a href="tel:8183819905" data-id="element-659" data-mp="8etck1jlqgea">(818) 381-9905</a>, or{' '}
        <Link to="/contact" data-id="element-660" data-mp="cgeqlq7vh0q2">request a free quote online</Link>. Compare
        packages and pricing on our{' '}
        <Link to="/services" data-id="element-661" data-mp="h8o8qk2qwwhs">detailing services page</Link>, or{' '}
        <Link to="/locations" data-id="element-662" data-mp="m6gd4dkkub2v">view all service areas</Link>.
      </p>
    </ServicePageLayout>;
}